import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { timer } from "rxjs";
import { takeWhile } from "rxjs/operators";
import { SubSink } from "subsink";
import { MessagingService } from "../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../core/messaging/severity-type.enum";
import { ClipboardService } from "../../../../core/storage/clipboard.service";
import { MemberDataLoadState } from "../../../api/member-v2/member-data-load-state-modal";
import { BulkLoadRequestStatus, BulkLoadValidationStatus } from "../../../api/member-v2/member-dataload-status.enum";
import { LoadSummaryResponse } from "../../../api/member-v2/member-dataload-summary.model";
import { MemberV2Service } from "../../../api/member-v2/member-v2.service";
import { DataLoadState } from "../data-load-state.model";
import { DataLoadService } from "../data-load.service";

@Component({
    selector: "app-member-data-load-summary",
    templateUrl: "./member-dataload-summary.component.html",
    styleUrls: ["./member-dataload-summary.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClipboardService],

})
export class MemeberDataLoadSummaryComponent implements OnInit, OnDestroy {
    private sink = new SubSink();
    memberLoadState: MemberDataLoadState;
    dataLoadState: DataLoadState;
    summaryReport: { key: string; value: number | string }[] = [];
    validationStatus: string;
    disableValidationReportBtn = false;
    isSubmissionComplete = false;
    isProcessingVisible = false;

    constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private memberService: MemberV2Service,
        private dataLoadService: DataLoadService,
        private readonly messagingService: MessagingService,
        private clipboard: ClipboardService
    ) { }

    ngOnInit() {
        this.sink.add(
            this.memberService.state.subscribe(state => {
                this.memberLoadState = state;
                this.changeDetector.markForCheck();
            }),
            this.dataLoadService.state.subscribe(state => {
                this.dataLoadState = state;
                this.changeDetector.markForCheck();
            })
        );
        this.loadSummaryData();

    }

    private loadSummaryData(): void {
        this.memberService.getMemberBulkLoadSummary(this.memberLoadState.dataLoadId)
            .subscribe(result => {
                this.summaryReport = this.mapLoadSummaryToKeyValue(result);
                this.changeDetector.markForCheck();
            });
    }

    ngOnDestroy() {
        this.sink.unsubscribe();
    }

    get currentDataLoadId(): string {
        return this.memberLoadState.dataLoadId;
    }

    get canLoadMembers(): boolean {
        return (this.validationStatus === BulkLoadValidationStatus.PASSED || this.validationStatus === BulkLoadValidationStatus.PARTIALLY_FAILED) && !this.isSubmissionComplete;
    }

    get canDownloadReport(): boolean {
        return (this.validationStatus === BulkLoadValidationStatus.FAILED || this.validationStatus === BulkLoadValidationStatus.PARTIALLY_FAILED) && !this.memberLoadState.isFileHeaderError;
    }

    get processHeaderText(): string {
        return "DATA UPLOAD IN PROGRESS... YOU MAY LEAVE THIS PAGE BY PRESSING THE ESC BUTTON. YOU WILL BE NOTIFIED WHEN YOUR UPLOAD IS COMPLETE.";
    }

    get shortLoadId(): string {
        return this.currentDataLoadId?.split("-").pop() ?? "";
    }

    get isLoadProcess(): boolean {
        return this.memberLoadState.isLoadProcess;
    }

    get summaryHeader(): string {
        return this.isLoadProcess ? "IMPORT SUMMARY" : "DATA LOAD SUMMARY";
    }

    private getValidationStatus(response: LoadSummaryResponse): string {
        if (response.validationErrorCount === 0) { return BulkLoadValidationStatus.PASSED; }
        if (response.validationErrorCount === response.totalCount) { return BulkLoadValidationStatus.FAILED; }
        return BulkLoadValidationStatus.PARTIALLY_FAILED;
    }

    private parseLoadDate(input: string): string {
        const [datePart, timePart] = input.replace("Z", "").split("T");
        const [yyyy, mm, dd] = datePart.split("-");
        return `${mm}/${dd}/${yyyy} ${timePart}`;
    }

    private mapLoadSummaryToKeyValue(response: LoadSummaryResponse): { key: string; value: number | string }[] {
        const isFileHeaderError = this.memberLoadState.isFileHeaderError;
        this.validationStatus = isFileHeaderError ? BulkLoadValidationStatus.FAILED : this.getValidationStatus(response);

        const keyValuePairs: { key: string; value: number | string }[] = [
            { key: "Load Date", value: this.parseLoadDate(response.loadCreateTs) },
            { key: "Members Validated", value: response.totalCount },
            { key: "Members Passed", value: response.submissionReadyCount },
            { key: "Members Failed", value: response.validationErrorCount },
            { key: "Members with Warnings", value: response.validationWarningCount },
            { key: "Validation Status", value: this.validationStatus },
        ];

        if (isFileHeaderError) {
            keyValuePairs.push({ key: "Error Reason", value: "File Header Exception" });
        }

        return keyValuePairs;
    }

    trackByIndex(index: number): number {
        return index;
    }

    getValidationMessageClass(value: string): string {
        switch (value) {
            case BulkLoadValidationStatus.FAILED:
            case BulkLoadValidationStatus.PARTIALLY_FAILED:
                return "summary-body__details--danger";
            case BulkLoadValidationStatus.PASSED:
                return "summary-body__details--pass";
            default:
                return "summary-body__details";
        }
    }

    onLoadMembers(): void {
        this.memberService.submitBulkLoadMembers({ loadId: this.currentDataLoadId })
            .subscribe(result => {
                this.isProcessingVisible = true;
                this.checkMemberLoadProgress(this.currentDataLoadId);
                this.changeDetector.markForCheck();
            });
    }

    onDownloadValidationReport(): void {
        this.memberService.getMemberBulkLoadValidationReportUrl(this.currentDataLoadId)
            .subscribe(result => {
                this.downloadReport(result);
            });
    }

    checkMemberLoadProgress(loadId: string): void {
        timer(0, 2000).pipe(
            takeWhile(() => !this.isSubmissionComplete)
        ).subscribe(() => {
            this.isMemberBulkLoadDone(loadId);
        });
    }

    private isMemberBulkLoadDone(loadId: string): void {
        this.memberService.getMemberBulkLoadStatus(loadId)
            .subscribe(result => {
                if (result.status === BulkLoadRequestStatus.SubmissionComplete) {
                    this.isSubmissionComplete = true;
                    this.isProcessingVisible = false;

                    this.memberService.setState({
                        isLoadProcess: false,
                    });

                    this.dataLoadService.setState({
                        isLoadProcess: false,
                    });

                    this.messagingService.showToast(
                        "Members Submitted Successfully.",
                        SeverityType.SUCCESS
                    );
                }
                this.changeDetector.markForCheck();
            });
    }

    async downloadReport(presignedUrl: string): Promise<void> {
        this.disableValidationReportBtn = true;

        this.messagingService.showToast(
            "Downloading is in progress.",
            SeverityType.SUCCESS
        );

        try {
            const fileName = presignedUrl.split("/").pop()?.split("?")[0] ?? "download";

            const link = document.createElement("a");
            link.href = presignedUrl;
            link.download = fileName;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            this.messagingService.showToast(
                "Error while downloading Validation Report.",
                SeverityType.ERROR
            );
        } finally {
            this.disableValidationReportBtn = false;
            this.changeDetector.detectChanges();
        }
    }

    copyLoadId(): void {
        this.clipboard.copy(this.currentDataLoadId);
    }

}
